import React, { useMemo } from 'react';

import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import EntityName from 'components/Globals/EntityName';
import { usePaywallContext } from 'components/Paywall';

import { DATE_FORMATS, TP } from 'constants/index';

import { createDate, createDatePath } from 'utils/date';
import useNavigate from 'utils/hooks/useNavigate';
import { formateYearMonthGroupDates } from 'utils/productions';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './PerformanceDatesVenues.module.scss';

const VenueCountryCity = ({ venue, isCancelled }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');

  if (!venue) {
    return (
      <Typography size={12} weight="medium" {...(isCancelled && { strikethrough: true })}>
        {t(`${TP}.FN_VENUE_NOT_ANNOUNCED`)}
      </Typography>
    );
  }

  return (
    <Typography variant="h5" className={classes.venueCountryCity}>
      <Typography size={12} color="secondary" {...(isCancelled && { strikethrough: true })}>
        {venue?.country?.name}
        {', '}
      </Typography>
      <Typography
        size={12}
        color="secondary"
        {...(isCancelled && { strikethrough: true })}
        className={classes.venueCountryCity__city}
      >
        {venue?.city?.name}
      </Typography>
    </Typography>
  );
};

const PerformanceDatesVenues = ({ data, trackingData }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const navigate = useNavigate();
  const { setIsPaywallExpanded } = usePaywallContext();

  const performanceDates = useMemo(() => formateYearMonthGroupDates(data?.performances), [data]);
  const venues = useMemo(
    () => data?.venues.filter((venue, index, self) => index === self.findIndex(v => v.id === venue.id)),
    [data],
  );

  const linkProps = useMemo(() => navigate.getLinkProps({ entity: data, entityType: ENTITY_TYPES.PRODUCTION }), [
    data,
    navigate,
  ]);

  if (!performanceDates || Object.keys(performanceDates).length === 0) {
    return null;
  }

  return (
    <div className={classes.performanceDatesVenues}>
      <div className={classes.performanceDatesVenues__allPerformanceDates}>
        (
        {Object.entries(performanceDates).map(([year, months]) => (
          <div key={year} className={classes.groupedDates}>
            {Object.entries(months).map(([month, days], _monthIndex) => (
              // NOTE: This is commented out because we don't want to show the year prefix for the first month of the year
              // const isFirstMonthOfYear = _monthIndex === 0;
              // const yearPrefix = isFirstMonthOfYear ? `${year} ` : '';

              <div key={month} className={classes.groupedDates__month}>
                <Typography size={12} color="secondary" className={classes.groupedDates__month_name}>
                  {month}
                </Typography>
                <Typography className={classes.groupedDates__month_dates}>
                  <ol className={classes.datesList}>
                    {days
                      .sort((a, b) => a.day - b.day)
                      .map(({ fullDate, status }, index) => {
                        const performanceLinkProps = {
                          ...linkProps?.getSubPath({
                            path: createDatePath(fullDate),
                          }),
                        };

                        let dayDisplay = createDate(fullDate).format(DATE_FORMATS.DATE);

                        if (status === 'matinee') {
                          dayDisplay += 'm';
                        } else if (status === 'cancelled') {
                          dayDisplay += 'c';
                        }

                        return (
                          <li key={fullDate} className="p-performance">
                            <LinkButton variant="native" isLink {...performanceLinkProps} preventDefault>
                              <Typography size={12} className={classes.date} color="secondary">
                                <time dateTime={createDate(fullDate).format(DATE_FORMATS.FULL_DATE)}>{dayDisplay}</time>
                                {index < days.length - 1 && ','}
                              </Typography>
                            </LinkButton>
                          </li>
                        );
                      })}
                  </ol>
                </Typography>
              </div>
            ))}
          </div>
        ))}
        )
      </div>
      <div className={classes.performanceDatesVenues__venues}>
        {venues?.length > 0 && (
          <div key={venues[0].id} className={classes.venue}>
            {venues.length > 1 ? (
              <Typography size={12} color="secondary" className={classes.moreVenues} blur={data?.isArchived}>
                {`+${venues.length} ${venues.length > 1 ? t(`${TP}.m_VENUES`) : t(`${TP}.m_VENUE`)}`}
              </Typography>
            ) : (
              <>
                <LinkButton
                  variant="native"
                  preventDefault
                  {...navigate.getLinkProps({
                    entity: venues[0],
                    entityType: ENTITY_TYPES.ORGANIZATION,
                  })}
                  isLink
                  {...(data?.isArchived && {
                    onClick: e => {
                      e.stopPropagation();
                      e.preventDefault();
                      setIsPaywallExpanded(true);
                    },
                  })}
                >
                  <SpriteIcon
                    icon="location_on_black"
                    size={12}
                    className={classes.locationIcon}
                    ariaLabel={t(`${TP}.m_ASKLOCATION`)}
                  />
                  <Typography
                    size={12}
                    color="secondary"
                    className={classes.venue_name}
                    key={`${venues[0]?.id}-${venues[0]?.stage?.id}`}
                    blur={data?.isArchived}
                  >
                    <EntityName
                      entityType={ENTITY_TYPES.ORGANIZATION}
                      entity={venues[0]}
                      trackingData={trackingData}
                      isRaw
                    />
                  </Typography>
                </LinkButton>
                <Typography size={12} color="secondary" className={classes.venue__countryCity} blur={data?.isArchived}>
                  <VenueCountryCity venue={venues[0]} isCancelled={data?.isCancelled} />
                </Typography>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceDatesVenues;
